import { useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { ArrowLeft, Cross, Delete, Files } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import { EdgeUI, NodeUI } from 'modules/workflows/components/Node/types'
import { useWorkflowContext } from 'modules/workflows/contexts/WorkflowContext'

type Props = {
  nodeId: string
  handleDelete?: () => void
  onClose?: () => void
  /** When provided, show a back arrow instead of the icon; click goes back (e.g. to Multi Action sidepanel). */
  onBack?: () => void
  title: string
  icon: React.ReactNode
  backgroundColor: string
  showDeleteButton?: boolean
  showDuplicateButton?: boolean
}

// Title bar and duplicate & delete icons
export const SidepanelHeader = ({
  nodeId,
  handleDelete: handleDeleteProp,
  onClose,
  onBack,
  title,
  icon,
  backgroundColor,
  showDeleteButton = true,
  showDuplicateButton = true,
}: Props) => {
  const { openDialog } = useConfirmationDialog()
  const { setEdgesUI, setNodesUI, nodesUI, edgesUI } = useWorkflowContext()

  const handleDuplicate = () => {
    const currentNode = nodesUI.find((node) => node.id === nodeId)
    if (!currentNode) return

    const newId = crypto.randomUUID()
    const offset = 150

    const duplicatedNode: NodeUI = {
      ...currentNode,
      id: newId,
      position: {
        x: currentNode.position.x,
        y: currentNode.position.y + offset,
      },
      selected: false,
    }

    const newNodes: NodeUI[] = [duplicatedNode]
    const newEdges: EdgeUI[] = []

    if (currentNode.type === 'condition') {
      const childNodes = nodesUI.filter((node) => {
        const isChild = edgesUI.some((edge) => edge.source === nodeId && edge.target === node.id)
        return isChild && (node.type === 'conditionIf' || node.type === 'conditionElse')
      })

      childNodes.forEach((childNode) => {
        const childNewId = crypto.randomUUID()
        const duplicatedChild: NodeUI = {
          ...childNode,
          id: childNewId,
          position: {
            x: childNode.position.x,
            y: childNode.position.y + offset,
          },
          selected: false,
        }
        newNodes.push(duplicatedChild)

        const originalEdge = edgesUI.find(
          (edge) => edge.source === nodeId && edge.target === childNode.id
        )
        if (originalEdge) {
          newEdges.push({
            ...originalEdge,
            id: `${newId}-${childNewId}`,
            source: newId,
            target: childNewId,
          })
        }
      })
    }

    setNodesUI((prev) => [...prev, ...newNodes])

    if (newEdges.length > 0) {
      setEdgesUI((prev) => [...prev, ...newEdges])
    }
  }

  const handleDelete =
    handleDeleteProp ??
    (() => {
      openDialog({
        title: i18n.t('workflows.sidepanel.general.delete_confirmation.title'),
        description: i18n.t('workflows.sidepanel.general.delete_confirmation.description'),
        confirmLabel: i18n.t('common.delete'),
        onConfirm: () => {
          setEdgesUI((prevEdges) => {
            // Find parent edges (edges pointing TO the node being deleted)
            const parentEdges = prevEdges.filter((edge) => edge.target === nodeId)

            // Find child edges (edges FROM the node being deleted)
            const childEdges = prevEdges.filter((edge) => edge.source === nodeId)

            // Create new edges connecting parents directly to children
            const reconnectionEdges = parentEdges.flatMap((parentEdge) =>
              childEdges.map((childEdge) => ({
                ...parentEdge,
                id: `${parentEdge.source}-${childEdge.target}`,
                target: childEdge.target,
              }))
            )

            // Remove all edges connected to the deleted node and add reconnection edges
            return [
              ...prevEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId),
              ...reconnectionEdges,
            ]
          })

          // Remove the node from nodesUI
          setNodesUI((prevNodes) => prevNodes.filter((node) => nodeId !== node.id))
        },
      })
    })

  return (
    <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" gap="s12">
      <Box flexDirection="row" alignItems="center" gap="s12">
        {onBack ? (
          <F0Button
            onClick={onBack}
            size="md"
            icon={ArrowLeft}
            hideLabel
            variant="outline"
            label={i18n.t('common.back')}
          />
        ) : (
          <Box
            flexDirection="row"
            style={{ backgroundColor: backgroundColor }}
            borderRadius={{ all: 'abs012' }}
            width="s32"
            height="s32"
            alignItems="center"
            justifyContent="center"
          >
            {icon}
          </Box>
        )}
        <Text color="textPrimary" size="300" weight="medium">
          {title}
        </Text>
      </Box>
      <Box flexDirection="row" gap="s8" justifyContent="center" alignItems="center">
        {showDuplicateButton && (
          <F0Button
            onClick={handleDuplicate}
            size="md"
            icon={Files}
            hideLabel
            variant="outline"
            label={i18n.t('common.duplicate')}
          />
        )}
        {showDeleteButton && (
          <F0Button
            onClick={handleDelete}
            size="md"
            icon={Delete}
            hideLabel
            variant="critical"
            label={i18n.t('common.delete')}
          />
        )}
        {onClose && (
          <>
            <Box width="s1" height="s20" style={{ backgroundColor: '#E2E2E5' }} />
            <F0Button
              onClick={onClose}
              size="md"
              icon={Cross}
              hideLabel
              variant="outline"
              label={i18n.t('common.close')}
            />
          </>
        )}
      </Box>
    </Box>
  )
}
